import { apiClient } from '@/core/infra_api_client'
import { staffRolloutPath } from './rollout_target'

/**
 * The Agion-managed chat address card (PLAN-20261015 slice 15,
 * agion/Agion#3473; ADR-003 G1, G7, G15): per organisation, the switch that
 * lets its admins make chat addresses Agion runs for them, and the most
 * addresses it may have. Only the staff route serves it, for the active
 * organisation as for any other: GET/PUT
 * /v1/infra/staff/rollout/managed-chat-address/organizations/{id}, forwarded
 * by infra-service to governance behind the verified platform administrator
 * check. The key health of Agion's two Cloudflare keys is the platform's,
 * not an organisation's: GET
 * /v1/infra/staff/rollout/managed-chat-address/key-health. The server
 * validates every limit and decides every refusal.
 */
export type ManagedChatAddressLevel = 'off' | 'on'

/** The card's limit as the server answers it. */
export interface ManagedChatAddressLimit {
  /** The organisation's own limit; null when it uses the default. */
  own: number | null
  /** The catalogue default. */
  default: number
  /** The most staff may set. */
  maximum: number
  /** The limit that applies now. */
  effective: number
}

export interface ManagedChatAddressPolicy {
  organizationId: string
  level: ManagedChatAddressLevel
  revision: number
  addressLimit: ManagedChatAddressLimit
}

/**
 * A history row. A limit left out is the catalogue default (the server omits
 * a null limit).
 */
export interface ManagedChatAddressHistoryEntry {
  organizationId: string
  revision: number
  previousLevel: ManagedChatAddressLevel
  level: ManagedChatAddressLevel
  previousAddressLimit?: number
  addressLimit?: number
  actorId: string
  reason: string
  changedAt: string
}

interface ManagedChatAddressHistoryResponse {
  history: ManagedChatAddressHistoryEntry[]
}

/**
 * A card change. `addressLimit` left out keeps the organisation's limit, null
 * returns it to the default, a number sets it.
 */
export interface ChangeManagedChatAddressPolicy {
  expectedRevision: number
  level: ManagedChatAddressLevel
  addressLimit?: number | null
  reason: string
  confirmed: true
}

/** One Cloudflare key's latest check, never the key itself. */
export interface ManagedChatKeyHealth {
  /** tunnel or dns. */
  key: string
  /**
   * Cloudflare's own status (active, disabled, expired), or unchecked,
   * unverified (the last check failed) or unavailable (not set up here).
   */
  status: string
  /** Cloudflare's id of the key; set once Cloudflare has answered a check. */
  keyId?: string
  /** Null for a key without expiry, or before Cloudflare answered one. */
  expiresOn: string | null
  lastCheckedAt: string | null
  lastRefusalCode?: string
  lastRefusalAt?: string
  /** True from the catalogued number of days before expiresOn. */
  expiryWarning: boolean
}

export interface ManagedChatKeysHealth {
  /** False when this environment's Cloudflare setup is missing or malformed. */
  available: boolean
  /** The missing or malformed configuration names when not available. */
  missingConfiguration: string[]
  /** True when both keys can be used now. */
  usable: boolean
  expiryWarning: boolean
  expiryWarningDays: number
  checkIntervalMs: number
  keys: ManagedChatKeyHealth[]
}

const card = 'managed-chat-address'
const keyHealthPath = `staff/rollout/${card}/key-health`

function cardPolicy(policy: ManagedChatAddressPolicy): ManagedChatAddressPolicy {
  if (!policy.addressLimit) {
    throw new Error('The managed chat address card answer has no address limit')
  }
  return policy
}

export async function getManagedChatAddressPolicy(organizationId: string): Promise<ManagedChatAddressPolicy> {
  return cardPolicy(await apiClient.get<ManagedChatAddressPolicy>(staffRolloutPath(card, organizationId)))
}

export async function listManagedChatAddressHistory(organizationId: string): Promise<ManagedChatAddressHistoryEntry[]> {
  const response = await apiClient.get<ManagedChatAddressHistoryResponse>(staffRolloutPath(card, organizationId, 'history'))
  return response.history ?? []
}

export async function changeManagedChatAddressPolicy(
  organizationId: string,
  command: ChangeManagedChatAddressPolicy,
): Promise<ManagedChatAddressPolicy> {
  return cardPolicy(await apiClient.put<ManagedChatAddressPolicy, ChangeManagedChatAddressPolicy>(
    staffRolloutPath(card, organizationId),
    command,
  ))
}

export async function getManagedChatKeyHealth(): Promise<ManagedChatKeysHealth> {
  const health = await apiClient.get<ManagedChatKeysHealth>(keyHealthPath)
  return {
    ...health,
    missingConfiguration: health.missingConfiguration ?? [],
    keys: health.keys ?? [],
  }
}
